import React, { useEffect, useState } from 'react';
import KeyboardArrowDownIcon from '@mui/icons-material/South';
import KeyboardArrowUpIcon from '@mui/icons-material/North';
import { useDebounce } from 'usehooks-ts';
import PlusIcon from '@mui/icons-material/Add';

import { CONTENTS_SHARED_WITH_ME, VIDEOFEED } from '../../../../enums/TYPES';
import SearchInput from '../../../modules/SearchInput';
import styles from './SharedWithMe.module.scss';
import { PaginationFooter } from '../../../modules/PaginationFooter';
import { getSharedComposers } from '../../../../api';
import Composer from '../../../MyReflexComposers/Composer';
import LinearProgress from '../../../modules/LinearProgress';
import { useAppSelector } from '../../../../store/hooks';
import { SortOptions, SortType } from '../../../../enums/SORT';
import { ContainedButton } from '../../../modules/Buttons';

type TSharedComposerListModal = {
  onSelect: (id: string) => void;
  disableMyFoldersInSidebar: boolean;
  onCreateComposer: () => void;
  canCreateComposer?: boolean;
};

const SharedComposerListModal: React.FC<TSharedComposerListModal> = ({
  onSelect,
  disableMyFoldersInSidebar,
  onCreateComposer,
  canCreateComposer = true,
}) => {
  const [sharedContent, setSharedContent] = useState<CONTENTS_SHARED_WITH_ME[] | VIDEOFEED[]>([]);
  const [sort, setSort] = useState<SortType>(SortType.ASC);
  const [sortOption, setSortOption] = useState<SortOptions>(SortOptions.NAME);
  const [loading, setLoading] = useState(false);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const debouncedValue = useDebounce<string>(search, 500);

  const { shareFolderForSharedWithMe } = useAppSelector((state) => state.folderStore);
  const { currentUser } = useAppSelector((state) => state.user);
  const { shareComposer } = useAppSelector((state) => state.composersStore);

  const fetchSharedContent = async () => {
    setLoading(true);
    try {
      const res = await getSharedComposers({
        page,
        limit: rowsPerPage,
        sort,
        search,
        sortOption,
      });

      setSharedContent(res.data.documents);
      setTotal(res.data.total);
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSharedContent();
  }, [page, rowsPerPage, debouncedValue, sort, sortOption, shareFolderForSharedWithMe, shareComposer]);

  const searchContent = (e: React.ChangeEvent<HTMLInputElement>) => {
    setLoading(true);
    e.preventDefault();
    setSearch(e.target.value);
    setPage(0);
  };

  const onRowsChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };

  const onPaginate = (e: React.MouseEvent<HTMLButtonElement, MouseEvent> | null, newPage: number) => {
    setPage(newPage);
  };

  const onHeaderSort = (option: SortOptions) => {
    setSortOption(option);
    setSort(sort === SortType.ASC ? SortType.DESC : SortType.ASC);
    setPage(0);
  };

  return (
    <div className={styles.container}>
      <div className={styles.toolbar}>
        <SearchInput onChange={searchContent} />
        <ContainedButton className={styles.createComposerBtn} onClick={onCreateComposer} disabled={!canCreateComposer}>
          <PlusIcon fontSize="small" />
          Create New Composer
        </ContainedButton>
      </div>
      {loading ? (
        <LinearProgress />
      ) : sharedContent.length > 0 ? (
        <div>
          <div className={styles.listHeader}>
            <div className={styles.name}>
              <button type="button" className={styles.headerBtn} onClick={() => onHeaderSort(SortOptions.NAME)}>
                Name
                {sortOption === SortOptions.NAME && sort === SortType.ASC ? <KeyboardArrowDownIcon className={styles.arrow} /> : null}
                {sortOption === SortOptions.NAME && sort === SortType.DESC ? <KeyboardArrowUpIcon className={styles.arrow} /> : null}
              </button>
            </div>
            <div className={styles.owner} onClick={() => onHeaderSort(SortOptions.OWNER)}>
              Owner
              {sortOption === SortOptions.OWNER && sort === SortType.ASC ? <KeyboardArrowDownIcon className={styles.arrow} /> : null}
              {sortOption === SortOptions.OWNER && sort === SortType.DESC ? <KeyboardArrowUpIcon className={styles.arrow} /> : null}
            </div>
            <div className={styles.date} onClick={() => onHeaderSort(SortOptions.UPDATED_AT)}>
              Last Modified
              {sortOption === SortOptions.UPDATED_AT && sort === SortType.DESC ? <KeyboardArrowDownIcon className={styles.arrow} /> : null}
              {sortOption === SortOptions.UPDATED_AT && sort === SortType.ASC ? <KeyboardArrowUpIcon className={styles.arrow} /> : null}
            </div>
            <div className={styles.size}>File Size</div>
          </div>
          <div className={styles.list}>
            {sharedContent.map((value, feedIndex) => (
              <div
                key={feedIndex}
                className="mb-5"
                style={{
                  filter:
                    value?.access?.find((acc) => acc?.user?.id === currentUser?.id)?.access.title === 'EDIT_AND_RE_SHARE'
                      ? ''
                      : 'brightness(70%)',
                }}
                onClick={
                  value?.access?.find((acc) => acc?.user?.id === currentUser?.id)?.access.title === 'EDIT_AND_RE_SHARE'
                    ? () => onSelect(value.id)
                    : console.log
                }
              >
                <Composer feedIndex={feedIndex} composer={value as VIDEOFEED} owner={false} sharedComposer noActions={true} />
              </div>
            ))}
          </div>
          <PaginationFooter
            page={page}
            total={total}
            onPageChange={onPaginate}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={onRowsChange}
          />
        </div>
      ) : (
        <div className={styles.noSharedContentContainer}>
          <div className={styles.noSharedContentText}>No shared content.</div>
        </div>
      )}
    </div>
  );
};

export default SharedComposerListModal;
